import type { NavGraph, NavNode, POI, Route, Vec3 } from '../types';
import { dist3 } from '../utils/math';
import { getGraph } from './graphService';
import { findNavmeshPathEx, isNavmeshLoaded, snapToNavmesh, getNavmeshDebugData, CAMERA_HEIGHT_M } from './navmeshService';
import { getFloorLinks, type FloorLink } from './floorPlanService';
import { loadConfig } from './configService';
import { logRoute, logEvent } from './debugLog';

/**
 * Route calculation over the navigation graph using A*.
 * Independent of MultiSet and of rendering — operates purely in map space.
 *
 * Multi-floor: nodes carry `floor` and edges can be 'elevator' | 'stairs' |
 * 'escalator'. A* works across floors as long as connecting edges exist.
 * Extension points:
 * - accessibleOnly: skips edges with accessible === false
 * - restricted areas: give edges kind 'restricted' and filter here
 */

export interface RouteOptions {
  accessibleOnly?: boolean;
}

interface AdjacencyEntry {
  node: NavNode;
  weight: number;
}

function buildAdjacency(graph: NavGraph, opts: RouteOptions): Map<string, AdjacencyEntry[]> {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const adj = new Map<string, AdjacencyEntry[]>();

  const add = (fromId: string, toId: string, weight: number) => {
    const to = byId.get(toId);
    if (!to) return;
    if (!adj.has(fromId)) adj.set(fromId, []);
    adj.get(fromId)!.push({ node: to, weight });
  };

  for (const edge of graph.edges) {
    if (opts.accessibleOnly && edge.accessible === false) continue;
    if (edge.kind === 'restricted') continue;
    const a = byId.get(edge.from);
    const b = byId.get(edge.to);
    if (!a || !b) continue;
    const w = edge.weight ?? dist3(a.position, b.position);
    add(edge.from, edge.to, w);
    if (edge.bidirectional !== false) add(edge.to, edge.from, w);
  }
  return adj;
}

/** A* from startNodeId to goalNodeId. Returns ordered nodes or null. */
export function findPath(
  graph: NavGraph,
  startNodeId: string,
  goalNodeId: string,
  opts: RouteOptions = {},
): NavNode[] | null {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const start = byId.get(startNodeId);
  const goal = byId.get(goalNodeId);
  if (!start || !goal) return null;

  const adj = buildAdjacency(graph, opts);
  const open = new Set<string>([start.id]);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>([[start.id, 0]]);
  const fScore = new Map<string, number>([[start.id, dist3(start.position, goal.position)]]);

  while (open.size > 0) {
    // Small graphs: linear scan is fine. Swap in a binary heap for 1000+ nodes.
    let current: string | null = null;
    let best = Infinity;
    for (const id of open) {
      const f = fScore.get(id) ?? Infinity;
      if (f < best) {
        best = f;
        current = id;
      }
    }
    if (!current) break;
    if (current === goal.id) {
      const path: NavNode[] = [byId.get(current)!];
      while (cameFrom.has(current)) {
        current = cameFrom.get(current)!;
        path.unshift(byId.get(current)!);
      }
      return path;
    }
    open.delete(current);
    const currentG = gScore.get(current) ?? Infinity;
    for (const { node, weight } of adj.get(current) ?? []) {
      const tentative = currentG + weight;
      if (tentative < (gScore.get(node.id) ?? Infinity)) {
        cameFrom.set(node.id, current);
        gScore.set(node.id, tentative);
        fScore.set(node.id, tentative + dist3(node.position, goal.position));
        open.add(node.id);
      }
    }
  }
  return null;
}

/** Nearest graph node to an arbitrary map-space position (same floor preferred). */
export function nearestNode(graph: NavGraph, pos: Vec3, floor?: number): NavNode | null {
  let bestNode: NavNode | null = null;
  let bestDist = Infinity;
  const candidates =
    floor !== undefined && graph.nodes.some((n) => n.floor === floor)
      ? graph.nodes.filter((n) => n.floor === floor)
      : graph.nodes;
  for (const n of candidates) {
    const d = dist3(n.position, pos);
    if (d < bestDist) {
      bestDist = d;
      bestNode = n;
    }
  }
  return bestNode;
}

/** Spacing between synthesized breadcrumb nodes on a direct-line route. */
const DIRECT_ROUTE_CRUMB_SPACING_M = 1.5;

/**
 * Fallback when the graph can't answer (no nodes, POI node missing, or no
 * connected path): a straight line of breadcrumb nodes from the user to the
 * POI. This keeps navigation working in venues where the nav graph hasn't
 * been authored yet — breadcrumbs always appear. Once real edges exist in
 * Supabase, A* takes precedence automatically.
 */
function directRoute(currentMapPos: Vec3, destination: POI): Route {
  const dest = destination.position;
  const total = dist3(currentMapPos, dest);
  const steps = Math.max(1, Math.floor(total / DIRECT_ROUTE_CRUMB_SPACING_M));

  const nodes: NavNode[] = [];
  for (let i = 1; i <= steps; i++) {
    const t = i / (steps + 1);
    nodes.push({
      id: `direct-${i}`,
      floor: destination.floor,
      position: {
        x: currentMapPos.x + (dest.x - currentMapPos.x) * t,
        y: currentMapPos.y + (dest.y - currentMapPos.y) * t,
        z: currentMapPos.z + (dest.z - currentMapPos.z) * t,
      },
      kind: 'direct',
    });
  }
  return { nodes, distance: total, destination };
}

/** Direct-line fallback is refused when start and POI differ in height by more than this — a straight line would cut through floors/ceilings. */
const DIRECT_ROUTE_MAX_DY_M = 2.0;

function routeFromPoints(points: Vec3[], currentMapPos: Vec3, destination: POI, kind: string): Route {
  // Bridge the start-snap gap: recast snaps the path start to the nearest
  // walkable polygon, which can land metres from the user's (drifting) pose.
  // Without this bridge the route begins away from the user's feet and every
  // off-path check reads ≈gap metres → endless off-route recalcs that return
  // the same route (seen in debug report 1783803692286: 5.5m off, 3× recalc,
  // identical 10.4m route each time).
  if (points.length > 0) {
    const gap = Math.hypot(points[0].x - currentMapPos.x, points[0].z - currentMapPos.z);
    if (gap > 0.75) {
      logEvent('route', `start-snap gap ${gap.toFixed(1)}m — bridging from user position`);
      points = [{ x: currentMapPos.x, y: points[0].y, z: currentMapPos.z }, ...points];
    }
  }
  const nodes: NavNode[] = points.map((p, i) => ({
    id: `${kind}-${i}`,
    floor: destination.floor,
    position: p,
    kind,
  }));
  let distance = dist3(currentMapPos, nodes[0].position);
  for (let i = 1; i < nodes.length; i++) {
    distance += dist3(nodes[i - 1].position, nodes[i].position);
  }
  distance += dist3(nodes[nodes.length - 1].position, destination.position);
  return { nodes, distance, destination };
}

/** Vertical tolerance for matching a floor-link endpoint to a floor (m). */
const LINK_FLOOR_TOL_M = 2.2;

// ---------- Gap hopping (same-floor scan holes) ----------

/** Max width of a scan hole the router may hop across (m). */
const GAP_HOP_MAX_M = 3.0;
/** Max hops per route — more than this means the mesh is truly fragmented. */
const GAP_HOPS_MAX = 3;

/**
 * Navmesh routing that HOPS small holes (user 2026-07-12: "can the logic go
 * to nearby navmesh if it's broken?"). Scan holes — dark doorways, thin
 * corridors, glass — split the walkable mesh into islands even though the
 * real floor is continuous. When a path dead-ends at a hole's edge, probe
 * just beyond it toward the destination, land on the neighbouring island at
 * the SAME height (±1.2 m — a hop can never cross floors), and continue.
 * Guards: gaps ≤3 m, ≤3 hops, every hop must actually get closer to the
 * destination (same-edge snaps are discarded), and each segment inherits
 * findNavmeshPathEx's own through-slab rejection.
 */
function tryGapHops(fromCamera: Vec3, to: Vec3): { pts: Vec3[]; hops: number } | null {
  const pts: Vec3[] = [];
  let cam = fromCamera;
  let hops = 0;
  let prevShortfall = Infinity;
  for (let iter = 0; iter <= GAP_HOPS_MAX; iter++) {
    const seg = findNavmeshPathEx(cam, to);
    if (!seg || seg.path.length === 0) return null;
    pts.push(...seg.path);
    if (seg.complete) {
      if (hops > 0) logEvent('route', `gap-hopped ${hops}× across navmesh holes`);
      return { pts, hops };
    }
    if (iter === GAP_HOPS_MAX) break;
    const shortfall = seg.endGapXZ + seg.endGapY;
    if (shortfall >= prevShortfall - 0.8) return null; // hop made no progress — give up
    prevShortfall = shortfall;

    // Probe just beyond the hole's edge, toward the destination.
    const end = seg.path[seg.path.length - 1];
    const dirX = to.x - end.x;
    const dirZ = to.z - end.z;
    const dl = Math.hypot(dirX, dirZ) || 1;
    let landed: Vec3 | null = null;
    for (let d = 0.6; d <= GAP_HOP_MAX_M; d += 0.4) {
      const s = snapToNavmesh(
        { x: end.x + (dirX / dl) * d, y: end.y, z: end.z + (dirZ / dl) * d },
        1.2,
      );
      if (!s) continue;
      if (Math.hypot(s.x - end.x, s.z - end.z) < 0.35) continue; // snapped back to the edge we're on
      if (Math.hypot(to.x - s.x, to.z - s.z) > Math.hypot(to.x - end.x, to.z - end.z) + 0.2) continue; // moved away
      landed = s;
      break;
    }
    if (!landed) return null;
    hops++;
    logEvent(
      'route',
      `gap hop ${hops}: hole edge (${end.x.toFixed(1)}, ${end.z.toFixed(1)}) → nearby island (${landed.x.toFixed(1)}, ${landed.z.toFixed(1)})`,
    );
    cam = { x: landed.x, y: landed.y + CAMERA_HEIGHT_M, z: landed.z };
  }
  return null;
}

// ---------- Auto floor-bridge (no authored 🪜 link yet) ----------

/**
 * When floors are disconnected AND nobody has marked a floor link, guess
 * where the two walkable islands physically connect: the closest
 * slope-plausible pair of walkable points between the user's floor and the
 * destination's floor. Stair flights are horizontally LONG for their rise
 * (~3-4 m run for ~3 m of rise), while a balcony edge directly above the
 * room below is nearly vertical — the slope guard (h ≥ 0.7·Δy) rejects the
 * balcony pair and keeps the stairs. Heuristic by nature: an authored link
 * always wins, and the log nudges staff to mark the real one.
 */
interface BridgeEnd {
  x: number;
  y: number;
  z: number;
}
const bridgeCache = new Map<string, { a: BridgeEnd; b: BridgeEnd } | null>();

/**
 * TEST SEAM: clears memoized route-layer state (the auto-bridge cache is
 * keyed only by floor heights, so tests that rebuild different synthetic
 * venues at the same heights would otherwise see stale bridges). Production
 * never needs this — a venue's geometry is fixed for the session.
 */
export function clearRouteCaches(): void {
  bridgeCache.clear();
}

function detectAutoBridge(userFootY: number, destY: number): { a: BridgeEnd; b: BridgeEnd } | null {
  const key = `${Math.round(userFootY * 2)}:${Math.round(destY * 2)}`;
  const cached = bridgeCache.get(key);
  if (cached !== undefined) return cached;

  const data = getNavmeshDebugData();
  if (!data) {
    bridgeCache.set(key, null);
    return null;
  }
  const { positions, indices } = data;
  // Bucket triangle centroids into a coarse XZ grid per floor band — the
  // pair search is O(A×B), so keep each band to a few hundred cells.
  const CELL = 0.75;
  const bandA = new Map<string, BridgeEnd>(); // user's floor
  const bandB = new Map<string, BridgeEnd>(); // destination's floor
  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i] * 3;
    const i1 = indices[i + 1] * 3;
    const i2 = indices[i + 2] * 3;
    const cx = (positions[i0] + positions[i1] + positions[i2]) / 3;
    const cy = (positions[i0 + 1] + positions[i1 + 1] + positions[i2 + 1]) / 3;
    const cz = (positions[i0 + 2] + positions[i1 + 2] + positions[i2 + 2]) / 3;
    const cell = `${Math.round(cx / CELL)}:${Math.round(cz / CELL)}`;
    if (Math.abs(cy - userFootY) < 1.2) {
      if (!bandA.has(cell)) bandA.set(cell, { x: cx, y: cy, z: cz });
    } else if (Math.abs(cy - destY) < 1.2) {
      if (!bandB.has(cell)) bandB.set(cell, { x: cx, y: cy, z: cz });
    }
  }
  // Cap the pair search for very large venues (subsample evenly).
  const cap = (m: Map<string, BridgeEnd>): BridgeEnd[] => {
    const arr = [...m.values()];
    const MAX = 900;
    if (arr.length <= MAX) return arr;
    const step = arr.length / MAX;
    const out: BridgeEnd[] = [];
    for (let i = 0; i < arr.length; i += step) out.push(arr[Math.floor(i)]);
    return out;
  };
  const as = cap(bandA);
  const bs = cap(bandB);
  let best: { a: BridgeEnd; b: BridgeEnd; d: number } | null = null;
  for (const a of as) {
    for (const b of bs) {
      const dy = Math.abs(a.y - b.y);
      if (dy < 1.8 || dy > 5.5) continue; // not a storey apart
      const h = Math.hypot(a.x - b.x, a.z - b.z);
      // Slope prior (Phase 2, INVARIANTS.md R3 — 21:16 session: the old
      // 0.7·Δy bound accepted a 1.6 m chord through the stairwell VOID onto
      // a scan fragment below floor level; the route under-read by metres).
      // Real stair flights run ≥ ~1.2·Δy horizontally (0.18 m risers /
      // 0.25 m treads ⇒ 1.4·Δy, minus landings). Steeper pairs are slab
      // edges/voids — skip them; U-shaped stairwells whose ends stack
      // vertically are exactly what the authored 🪜 link is for.
      if (h < dy * 1.2) continue;
      const d = Math.hypot(h, dy);
      if (!best || d < best.d) best = { a, b, d };
    }
  }
  const result = best ? { a: best.a, b: best.b } : null;
  if (result) {
    logEvent(
      'route',
      `auto floor-bridge detected: (${result.a.x.toFixed(1)}, ${result.a.y.toFixed(1)}, ${result.a.z.toFixed(1)}) ↔ (${result.b.x.toFixed(1)}, ${result.b.y.toFixed(1)}, ${result.b.z.toFixed(1)}) — mark the real stairs in Dashboard 🪜 for accuracy`,
    );
  } else {
    logEvent('route', 'auto floor-bridge: no slope-plausible connection found between the floors');
  }
  bridgeCache.set(key, result);
  return result;
}

/**
 * Composite cross-floor route through an AUTHORED floor link (stairs/
 * elevator/escalator marked with two taps in the Dashboard's 2D view):
 * navmesh leg to the link's near end → the link itself → navmesh leg from
 * the far end to the destination. This is how cross-floor routing works
 * when the generated navmesh leaves floors as disconnected islands —
 * stairwells are common scan/VPS dead zones (real venue, 2026-07-12:
 * "navmesh path is partial … floors likely disconnected").
 */
async function tryLinkedRoute(currentMapPos: Vec3, destination: POI): Promise<Route | null> {
  let links: FloorLink[] = [];
  try {
    links = await getFloorLinks(loadConfig().code);
  } catch {
    /* links unavailable — the auto-bridge below still gets a chance */
  }
  const userFootY = currentMapPos.y - CAMERA_HEIGHT_M;
  const destY = destination.position.y;

  let best: { pts: Vec3[]; total: number; link: FloorLink } | null = null;
  for (const link of links) {
    for (const [near, far] of [
      [link.a, link.b],
      [link.b, link.a],
    ] as const) {
      if (Math.abs(near.y - userFootY) > LINK_FLOOR_TOL_M) continue; // near end must be on the user's floor
      if (Math.abs(far.y - destY) > LINK_FLOOR_TOL_M) continue; // far end must be on the destination's floor
      // Legs gap-hop too — a scan hole between the user and the stairs
      // shouldn't kill the whole cross-floor route.
      const leg1 = tryGapHops(currentMapPos, near);
      if (!leg1 || leg1.pts.length === 0) continue;
      // tryGapHops treats its first arg as a CAMERA position — add the
      // camera height back so the far endpoint snaps onto its own floor.
      const leg2 = tryGapHops(
        { x: far.x, y: far.y + CAMERA_HEIGHT_M, z: far.z },
        destination.position,
      );
      if (!leg2 || leg2.pts.length === 0) continue;
      const pts = [...leg1.pts, near, far, ...leg2.pts];
      let total = 0;
      for (let i = 1; i < pts.length; i++) total += dist3(pts[i - 1], pts[i]);
      if (!best || total < best.total) best = { pts, total, link };
    }
  }
  if (!best) {
    // No authored link matched — fall back to the AUTO-DETECTED bridge
    // (user 2026-07-12: navigation must work before staff mark anything).
    if (Math.abs(userFootY - destY) <= LINK_FLOOR_TOL_M) return null; // same floor — not a link problem
    const bridge = detectAutoBridge(userFootY, destY);
    if (!bridge) return null;
    const leg1 = tryGapHops(currentMapPos, bridge.a);
    if (!leg1 || leg1.pts.length === 0) return null;
    const leg2 = tryGapHops(
      { x: bridge.b.x, y: bridge.b.y + CAMERA_HEIGHT_M, z: bridge.b.z },
      destination.position,
    );
    if (!leg2 || leg2.pts.length === 0) return null;
    const pts = [...leg1.pts, bridge.a, bridge.b, ...leg2.pts];
    const route = routeFromPoints(pts, currentMapPos, destination, 'navmesh');
    logRoute('navmesh+autobridge', route.nodes.length, route.distance, destination.name);
    return route;
  }
  // Node kind stays 'navmesh' so guidance respects per-point heights
  // (crumbs descend the link) — the link leg itself is a straight segment.
  const route = routeFromPoints(best.pts, currentMapPos, destination, 'navmesh');
  logEvent(
    'route',
    `linked route via ${best.link.kind} "${best.link.label}" (${best.link.a.y.toFixed(1)}m ↔ ${best.link.b.y.toFixed(1)}m)`,
  );
  logRoute('navmesh+link', route.nodes.length, route.distance, destination.name);
  return route;
}

/**
 * Full route from the user's current map-space position to a POI.
 * Priority:
 *  1. NAVMESH (public/navmesh.glb) — authored walkable surfaces; handles
 *     multi-floor via ramp/stair geometry, funnel-smoothed paths.
 *  2. GRAPH A* — authored nodes/edges from Supabase (multi-floor via
 *     elevator/stairs edges).
 *  3. DIRECT LINE — same-floor convenience fallback only; refused across
 *     floors so guidance never cuts vertically through the building.
 */
export async function calculateRoute(
  currentMapPos: Vec3,
  destination: POI,
  opts: RouteOptions = {},
): Promise<Route | null> {
  // Snap the POI to the walkable surface: stored POI heights are unreliable
  // (marked at furniture height, or during a drifted session — e.g. a POI at
  // y=3.18 labelled floor 0). Arrival checks and the destination pin then
  // operate on the real floor position under/near the POI.
  const snapped = snapToNavmesh(destination.position);
  if (snapped) destination = { ...destination, position: snapped };

  // 1) Navmesh — SKIPPED in accessible mode: the navmesh treats stairs as
  // walkable and can't distinguish them, so step-free routing must go through
  // the authored graph (elevator/accessible edges).
  if (!opts.accessibleOnly && isNavmeshLoaded()) {
    // Gap-hopping navmesh route: complete paths come straight through; small
    // same-floor scan holes are hopped (see tryGapHops).
    const hopped = tryGapHops(currentMapPos, destination.position);
    if (hopped && hopped.pts.length > 0) {
      const route = routeFromPoints(hopped.pts, currentMapPos, destination, 'navmesh');
      logRoute(hopped.hops > 0 ? 'navmesh+hop' : 'navmesh', route.nodes.length, route.distance, destination.name);
      return route;
    }
    // Disconnected floors? Stitch through an authored stairs/elevator link
    // (or the auto-detected bridge).
    const linked = await tryLinkedRoute(currentMapPos, destination);
    if (linked) return linked;
    logEvent('route', `navmesh loaded but no path to ${destination.name} — falling through`);
  } else if (opts.accessibleOnly) {
    logEvent('route', 'accessible mode — navmesh skipped, graph-only routing');
  }

  // 2) Graph A*
  const graph = await getGraph();
  const startNode = nearestNode(graph, currentMapPos);
  if (startNode) {
    const nodes = findPath(graph, startNode.id, destination.nodeId, opts);
    if (nodes && nodes.length > 0) {
      const route = routeFromPoints(nodes.map((n) => n.position), currentMapPos, destination, 'graph');
      logRoute('graph', route.nodes.length, route.distance, destination.name);
      return route;
    }
  }

  // 3) Direct line — same floor only
  if (Math.abs(currentMapPos.y - destination.position.y) > DIRECT_ROUTE_MAX_DY_M) {
    logEvent('route', `refused cross-floor direct route to ${destination.name} (Δy ${Math.abs(currentMapPos.y - destination.position.y).toFixed(1)}m)`);
    return null; // cross-floor with no navmesh/graph: refuse rather than mislead
  }
  const route = directRoute(currentMapPos, destination);
  logRoute('direct', route.nodes.length, route.distance, destination.name);
  return route;
}
